'use client';

import {
  useConfiguredBlockExplorer,
  useConfiguredChainId,
} from '#/data/wagmi/configured';
import { ellipsizeString } from '#/lib/ellipsize-string';
import { cn } from '#/lib/utils';
import { Translation } from 'intl-lib';
import { ExternalLink } from 'lucide-react';
import Link from 'next/link';
import { ReactNode } from 'react';
import { Address } from 'viem';
import { useBalance, useEnsName } from 'wagmi';
import { AnimatedNumber } from '../display/animated-number';
import { Button } from '../ui/button';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '../ui/hover-card';
import { CopyToClipboardButton } from '../v2/copy-to-clipboard';
import { AccountAvatar } from './account-avatar';

interface Props {
  title?: ReactNode;
  address?: Address | null;
  visibleChars?: number;
  className?: string;
  avatarClassName?: string;
}
export function AccountHoverCard({
  title,
  address,
  visibleChars,
  className,
  avatarClassName,
}: Props) {
  return (
    <HoverCard openDelay={0}>
      <HoverCardTrigger
        className={cn(
          'underline underline-offset-4 decoration-dashed decoration-gray-300 inline-flex items-center gap-1 cursor-pointer',
          className
        )}
      >
        <AccountAvatar
          address={address}
          className={cn('size-4 shrink-0', avatarClassName)}
        />
        {ellipsizeString(address, visibleChars)}
      </HoverCardTrigger>
      <HoverCardContent className="w-auto">
        <AccountHoverCardContent address={address} title={title} />
      </HoverCardContent>
    </HoverCard>
  );
}

interface AccountHoverCardContentProps {
  title?: ReactNode;
  address?: Address | null;
}

function AccountHoverCardContent({
  title,
  address,
}: AccountHoverCardContentProps) {
  const chainId = useConfiguredChainId();
  const blockExplorer = useConfiguredBlockExplorer();
  const { data: ensName } = useEnsName({
    chainId,
    address: address != null ? address : undefined,
  });
  const { data: balance, isLoading } = useBalance({
    chainId,
    address: address != null ? address : undefined,
  });
  return (
    <div className="flex flex-col gap-4 max-w-80">
      {title ? <h4 className="font-semibold">{title}</h4> : null}
      <div className="flex gap-4">
        <AccountAvatar
          address={address}
          diameter={20}
          className="size-10 shrink-0"
        />
        <div className="flex-1 flex flex-col gap-1">
          {ensName != null ? (
            <>
              <p className="text-sm uppercase font-semibold">{ensName}</p>
              <p className="text-sm break-all">
                {ellipsizeString(address, -1)}
              </p>
            </>
          ) : (
            <p className="text-sm break-all">{ellipsizeString(address, -1)}</p>
          )}
          <span className="text-sm text-muted-foreground">
            {balance != null ? (
              <AnimatedNumber
                value={balance.value}
                fractionDigits={5}
                suffix={` ${balance.symbol}`}
              />
            ) : (
              '-'
            )}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <CopyToClipboardButton text={address} variant="outline" size="sm">
          <Translation
            path="EthereumStaking.AccountHoverCard.CopyAddressButtonLabel"
            params={[blockExplorer?.name]}
          />
        </CopyToClipboardButton>
        <Button asChild variant="outline" size="sm">
          <Link
            href={`${blockExplorer?.url}/address/${address}`}
            target="_blank"
            rel="noreferrer"
            prefetch={false}
          >
            <ExternalLink />
            <Translation
              path="global.noun.view_on_button_label"
              params={blockExplorer}
            />
          </Link>
        </Button>
      </div>
    </div>
  );
}
